import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import { useHistory } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";
import {
  AlertCircle,
  Award,
  BadgeCheck,
  BookOpen,
  CheckCircle2,
  Brain,
  ChevronDown,
  Download,
  ExternalLink,
  FileText,
  Globe,
  GraduationCap,
  HelpCircle,
  Image as ImageIcon,
  KeyRound,
  Megaphone,
  Palette,
  RefreshCw,
  Scroll,
  Search,
  ShieldCheck,
  Type as TypeIcon,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import BadgePreview from "./BadgePreview";
import ProductPicker from "./ProductPicker";
import RulesTab from "./rules/RulesTab";
import Config from "../../_config/config";
import CredentialService from "../../_services/CredentialService";
import BadgeImagePicker from "./BadgeImagePicker";
import "./credential-admin.css";

// ── Types (unchanged from the original) ──────────────────────────────────
interface SigningKey {
  KeyId: string;
  Algorithm: string;
  PublicKeyMultibase: string;
  Status: string;
  CreatedDate: string;
  RotatedDate: string | null;
  SchoolId: number | null;
  SchoolName: string | null;
}

interface School {
  Id: number;
  Name: string;
}

interface CertTemplate {
  Id: number;
  Name: string;
  IsBuiltIn: number;
  PreviewImagePath: string | null;
  Category: string | null;
  SortOrder: number;
  EnableStatus: number;
}

interface CrossSellLink {
  label: string;
  url: string;
  imageUrl?: string;
  description?: string;
}

interface BadgeDef {
  Id: number;
  BadgeUUID: string;
  Name: string;
  Description: string | null;
  CriteriaText: string | null;
  CriteriaUrl: string | null;
  ImagePath: string | null;
  CertificateId: number | null;
  AchievementType: string;
  ExpiryDurationDays: number | null;
  CtaUrl: string | null;
  CtaText: string | null;
  VerifyTemplate: string | null;
  MetadataJSON: any;
  certificateName: string | null;
  CreatedDate: string;
}

// Legacy VerifyTemplate values map onto the four current page designs.
// Keep in sync with resolveTemplate in ilearn credential-verify/components/verify-utils.js.
const VERIFY_TEMPLATES = ["registrar", "ledger", "onyx", "folio"];
const LEGACY_VERIFY_TEMPLATE_MAP: Record<string, string> = {
  classic: "registrar",
  modern: "onyx",
  minimal: "folio",
  vibrant: "folio",
};
const resolveVerifyTemplate = (value: string | null): string => {
  const v = (value || "").toLowerCase();
  if (VERIFY_TEMPLATES.includes(v)) return v;
  return LEGACY_VERIFY_TEMPLATE_MAP[v] || "registrar";
};

interface StudentResult {
  enquiryId: number;
  name: string;
  email: string | null;
  studentCode: string | null;
}

interface IssueResult {
  credentialUUID: string;
  recipientName: string;
  badgeName: string;
  verificationUrl: string;
  signed: boolean;
  alreadyExisted: boolean;
}

interface IssuedCredential {
  Id: number;
  CredentialUUID: string;
  RecipientName: string;
  RecipientEmail: string | null;
  badgeName: string | null;
  certificateName: string | null;
  CourseName: string | null;
  IssuedDate: string;
  ExpiryDate: string | null;
  Status: number;
  verificationUrl: string;
}

/** Generate smart defaults for description & criteria based on name + badge type */
const formatIssuedDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Click-to-expand inline help next to a form label. Keeps the form scannable
 * for power users while making OB 3.0 plumbing fields self-explanatory for
 * first-time admins.
 */
const FieldHint: React.FC<{ summary: string; children: React.ReactNode }> = ({ summary, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={summary}
        className="bm-hint-btn"
        aria-expanded={open}
      >
        <HelpCircle size={14} />
      </button>
      {open && <div className="bm-hint-panel">{children}</div>}
    </>
  );
};

const ACHIEVEMENT_TYPE_OPTIONS = [
  { value: "Badge", label: "Badge", sub: "Mid-journey milestone", emoji: "🎖" },
  { value: "MicroCredential", label: "Micro-credential", sub: "Focused skill credential", emoji: "🧠" },
  { value: "Achievement", label: "Achievement", sub: "Excellence recognition", emoji: "🏆" },
  { value: "Recognition", label: "Recognition", sub: "Participation / contribution", emoji: "🤝" },
];

const CollapsibleSection: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bm-section bm-section-collapsible" data-open={open}>
      <div
        className="bm-section-head"
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((v) => !v); } }}
        aria-expanded={open}
      >
        <span className="bm-section-head-icon">{icon}</span>
        <h4>{title}</h4>
        {subtitle ? <span className="bm-section-sub">{subtitle}</span> : null}
        <ChevronDown size={16} className="bm-section-chevron" />
      </div>
      {open ? <div className="bm-section-body">{children}</div> : null}
    </div>
  );
};

const Section: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, children }) => (
  <div className="bm-section">
    <div className="bm-section-head">
      <span className="bm-section-head-icon">{icon}</span>
      <h4>{title}</h4>
      {subtitle ? <span className="bm-section-sub">{subtitle}</span> : null}
    </div>
    <div className="bm-section-body">{children}</div>
  </div>
);

const generateBadgeDefaults = (name: string, achievementType: string) => {
  const n = name.trim();
  if (!n) return { description: "", criteriaText: "" };

  const typeLabels: Record<string, string> = {
    Badge: "earning the milestone badge for",
    MicroCredential: "demonstrating proficiency in",
    Achievement: "achieving excellence in",
    Recognition: "being recognised for",
  };
  const verb = typeLabels[achievementType] || "earning the milestone badge for";

  return {
    description: `Awarded for ${verb} ${n}`,
    criteriaText:
      achievementType === "MicroCredential"
        ? `Student demonstrated the required competency in ${n}`
        : achievementType === "Achievement"
        ? `Student met the achievement criteria for ${n}`
        : achievementType === "Recognition"
        ? `Student was recognised for outstanding contribution to ${n}`
        : `Student earned the milestone badge for ${n}`,
  };
};

type TabKey = "keys" | "badges" | "issue" | "templates" | "rules";

// Built-in certificate templates are authored at a fixed 1056x816 canvas
// (US Letter landscape @ 96dpi). To preview without clipping, render the
// iframe at native size and scale it to fit the modal width.
const CERT_NATIVE_W = 1056;
const CERT_NATIVE_H = 816;

const ScaledCertificatePreview: React.FC<{ html: string }> = ({ html }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / CERT_NATIVE_W);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      style={{
        width: "100%",
        height: CERT_NATIVE_H * scale,
        overflow: "hidden",
        border: "1px solid var(--border-default)",
        borderRadius: 8,
      }}
    >
      <iframe
        srcDoc={html}
        title="Certificate Preview"
        style={{
          width: CERT_NATIVE_W,
          height: CERT_NATIVE_H,
          border: "none",
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          display: "block",
        }}
      />
    </div>
  );
};

// ── Applies-To scope picker (premium combobox) ──────────────────────────
type ScopeType = "lesson_plan" | "course" | "program";

const SCOPE_THEME: Record<ScopeType, { tint: string; ink: string }> = {
  lesson_plan: { tint: "#eef2ff", ink: "#4338ca" }, // indigo
  course:      { tint: "#ecfeff", ink: "#0e7490" }, // cyan
  program:     { tint: "#fef3c7", ink: "#92400e" }, // amber
};

interface ScopeResult {
  id: number;
  name: string;
  code?: string | null;
}

interface ScopePickerProps {
  scope: ScopeType;
  label: string;
  icon: React.ReactNode;
  attached: Array<{ scopeType: ScopeType; scopeId: number; scopeLabel: string }>;
  onAdd: (id: number, name: string) => void;
  onRemove: (id: number) => void;
}

const ScopePicker: React.FC<ScopePickerProps> = ({
  scope, label, icon, attached, onAdd, onRemove,
}) => {
  const theme = SCOPE_THEME[scope];
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<ScopeResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const reqIdRef = useRef(0);

  // Debounced fetch — empty term returns top 20 (browse mode).
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const myReq = ++reqIdRef.current;
      setLoading(true);
      CredentialService.searchEligibilityScope(scope, term.trim())
        .then((res: any) => {
          if (myReq !== reqIdRef.current) return; // ignore stale
          setResults(res?.data?.data || []);
        })
        .catch(() => { if (myReq === reqIdRef.current) setResults([]); })
        .finally(() => { if (myReq === reqIdRef.current) setLoading(false); });
    }, 220);
    return () => clearTimeout(t);
  }, [term, scope, open]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => { setHighlight(0); }, [results]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = results[highlight];
      if (!pick) return;
      const already = attached.some((s) => s.scopeId === pick.id);
      if (already) return;
      onAdd(pick.id, pick.name);
      setTerm("");
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const renderMatch = (name: string, q: string) => {
    const query = q.trim();
    if (!query) return name;
    const i = name.toLowerCase().indexOf(query.toLowerCase());
    if (i < 0) return name;
    return (
      <>
        {name.slice(0, i)}
        <mark className="at-mark">{name.slice(i, i + query.length)}</mark>
        {name.slice(i + query.length)}
      </>
    );
  };

  return (
    <div className="at-section" ref={wrapRef}>
      <div className="at-section-head">
        <div className="at-section-title">
          <span className="at-section-icon" style={{ background: theme.tint, color: theme.ink }}>
            {icon}
          </span>
          <span>{label}</span>
          {attached.length > 0 && (
            <span className="at-section-count" style={{ background: theme.tint, color: theme.ink }}>
              {attached.length}
            </span>
          )}
        </div>
        {attached.length > 0 && (
          <button
            type="button"
            className="at-clear-all"
            onClick={() => attached.forEach((s) => onRemove(s.scopeId))}
          >
            Clear all
          </button>
        )}
      </div>

      {attached.length > 0 && (
        <div className="at-chips">
          {attached.map((s) => (
            <span
              key={s.scopeId}
              className="at-chip"
              style={{ background: theme.tint, color: theme.ink }}
            >
              <span className="at-chip-label" title={`${s.scopeLabel} (id ${s.scopeId})`}>
                {s.scopeLabel}
              </span>
              <span className="at-chip-id">[{s.scopeId}]</span>
              <button
                type="button"
                className="at-chip-x"
                onClick={() => onRemove(s.scopeId)}
                style={{ color: theme.ink }}
                aria-label={`Remove ${s.scopeLabel}`}
              >
                <X size={12} strokeWidth={2.6} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="at-search">
        <Search size={14} className="at-search-icon" />
        <input
          ref={inputRef}
          type="text"
          className="at-search-input"
          placeholder={
            scope === "lesson_plan"
              ? `Search by name, code, or id — or click to browse`
              : `Search by name or id — or click to browse`
          }
          value={term}
          onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {loading && <span className="at-search-spinner" />}
        {!loading && term && (
          <button
            type="button"
            className="at-search-clear"
            onClick={() => { setTerm(""); inputRef.current?.focus(); }}
            aria-label="Clear search"
          >
            <X size={12} strokeWidth={2.6} />
          </button>
        )}
      </div>

      {open && (
        <div className="at-results" role="listbox">
          {loading && results.length === 0 ? (
            <div className="at-results-empty">
              <span className="at-results-spinner" /> Searching…
            </div>
          ) : results.length === 0 ? (
            <div className="at-results-empty">
              {term.trim()
                ? `No ${label.toLowerCase()} match "${term}"`
                : `No ${label.toLowerCase()} found`}
            </div>
          ) : (
            <>
              <div className="at-results-list">
                {results.map((r, i) => {
                  const already = attached.some((s) => s.scopeId === r.id);
                  const active = i === highlight;
                  return (
                    <div
                      key={r.id}
                      role="option"
                      aria-selected={active}
                      aria-disabled={already}
                      className={[
                        "at-result",
                        active ? "at-result-active" : "",
                        already ? "at-result-added" : "",
                      ].join(" ").trim()}
                      onMouseEnter={() => setHighlight(i)}
                      onMouseDown={(e) => {
                        e.preventDefault(); // keep input focus
                        if (already) return;
                        onAdd(r.id, r.name);
                        setTerm("");
                      }}
                    >
                      <span className="at-result-name">{renderMatch(r.name, term)}</span>
                      <span className="at-result-meta">
                        {r.code && (
                          <span className="at-result-code" title={r.code}>
                            {renderMatch(r.code, term)}
                          </span>
                        )}
                        <span className="at-result-id">[{r.id}]</span>
                        {already && (
                          <span className="at-result-added-pill">
                            <CheckCircle2 size={11} strokeWidth={2.5} /> Added
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="at-results-hint">
                <kbd>↑</kbd><kbd>↓</kbd>&nbsp;navigate
                <span className="at-hint-sep">·</span>
                <kbd>↵</kbd>&nbsp;add
                <span className="at-hint-sep">·</span>
                <kbd>esc</kbd>&nbsp;close
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const CredentialAdmin: React.FC = () => {
  const history = useHistory();

  // ── Keys state ──
  const [keys, setKeys] = useState<SigningKey[]>([]);
  const [keysLoading, setKeysLoading] = useState(true);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [showKeyConfirm, setShowKeyConfirm] = useState(false);

  // ── Badge definitions state ──
  const [badges, setBadges] = useState<BadgeDef[]>([]);
  const [badgesLoading, setBadgesLoading] = useState(true);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [editingBadge, setEditingBadge] = useState<BadgeDef | null>(null);
  const [badgeForm, setBadgeForm] = useState({
    name: "",
    description: "",
    criteriaText: "",
    criteriaUrl: "",
    achievementType: "Badge",
    expiryDurationDays: "",
    certificateId: "",
    imagePath: "",
    ctaUrl: "",
    ctaText: "",
    verifyTemplate: "registrar",
    crossSellLinks: [] as CrossSellLink[],
  });
  const [saving, setSaving] = useState(false);

  // ── Badge eligibility (polymorphic Applies To) state ──
  const [showAppliesToModal, setShowAppliesToModal] = useState(false);
  const [appliesToBadge, setAppliesToBadge] = useState<BadgeDef | null>(null);
  const [appliesToLoading, setAppliesToLoading] = useState(false);
  const [appliesToSaving, setAppliesToSaving] = useState(false);
  const [appliesToScopes, setAppliesToScopes] = useState<
    Array<{ scopeType: "lesson_plan" | "course" | "program"; scopeId: number; scopeLabel: string }>
  >([]);

  // ── DID Configuration state ──
  const [customIssuerDomain, setCustomIssuerDomain] = useState("");
  const [domainSaving, setDomainSaving] = useState(false);

  // ── Issue credentials state ──
  const [studentSearch, setStudentSearch] = useState("");
  const [studentResults, setStudentResults] = useState<StudentResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentResult | null>(null);
  const [selectedBadgeId, setSelectedBadgeId] = useState<number | null>(null);
  const [issueMode, setIssueMode] = useState<"badge" | "certificate">("badge");
  const [selectedIssueCertificateId, setSelectedIssueCertificateId] = useState<number | null>(null);
  const [issueCourseName, setIssueCourseName] = useState("");
  const [issuing, setIssuing] = useState(false);
  const [issueResult, setIssueResult] = useState<IssueResult | null>(null);
  const [issuedCredentials, setIssuedCredentials] = useState<IssuedCredential[]>([]);
  const [issuedLoading, setIssuedLoading] = useState(false);
  const [issuedPage, setIssuedPage] = useState(1);
  const [issuedTotal, setIssuedTotal] = useState(0);
  const [showIssueConfirm, setShowIssueConfirm] = useState(false);

  // ── Attendance badge bulk issue state ──
  const [attendanceCourseSearch, setAttendanceCourseSearch] = useState("");
  const [attendanceCourseResults, setAttendanceCourseResults] = useState<
    { courseId: number; name: string; code?: string }[]
  >([]);
  const [selectedCourse, setSelectedCourse] = useState<
    { courseId: number; name: string; code?: string } | null
  >(null);
  const [eligibleStudents, setEligibleStudents] = useState<any[]>([]);
  const [alreadyIssuedCount, setAlreadyIssuedCount] = useState(0);
  const [eligibleChecked, setEligibleChecked] = useState(false);
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [issuingBulk, setIssuingBulk] = useState(false);
  const [bulkResult, setBulkResult] = useState<{
    issued: number; skipped: number; courseName: string;
  } | null>(null);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  // ── Schools ──
  const [schools, setSchools] = useState<School[]>([]);
  const [keySchoolScope, setKeySchoolScope] = useState<number | "org">("org");

  // ── Certificate templates state ──
  const [templates, setTemplates] = useState<CertTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CertTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: "", content: "", category: "general", remarks: "",
  });
  const [templateSaving, setTemplateSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);

  // ── Shared ──
  const [activeTab, setActiveTab] = useState<TabKey>("keys");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ── Loaders ──
  const fetchKeys = useCallback(() => {
    setKeysLoading(true);
    CredentialService.listKeys()
      .then((res) => { if (res.data?.status === 1) setKeys(res.data.data || []); })
      .catch(() => setError("Failed to load signing keys"))
      .finally(() => setKeysLoading(false));
  }, []);

  const fetchBadges = useCallback(() => {
    setBadgesLoading(true);
    CredentialService.listBadgeDefinitions()
      .then((res) => { if (res.data?.status === 1) setBadges(res.data.data || []); })
      .catch(() => setError("Failed to load badge definitions"))
      .finally(() => setBadgesLoading(false));
  }, []);

  const fetchSchools = useCallback(() => {
    CredentialService.listSchools()
      .then((res) => { if (res.data?.status === 1) setSchools(res.data.data || []); })
      .catch(() => {});
  }, []);

  const fetchTemplates = useCallback(() => {
    setTemplatesLoading(true);
    CredentialService.listCertificateTemplates()
      .then((res) => { if (res.data?.status === 1) setTemplates(res.data.data || []); })
      .catch(() => setError("Failed to load certificate templates"))
      .finally(() => setTemplatesLoading(false));
  }, []);

  const fetchIssued = useCallback((page: number = 1) => {
    setIssuedLoading(true);
    CredentialService.listIssuedCredentials(page)
      .then((res) => {
        if (res.data?.status === 1) {
          setIssuedCredentials(res.data.data || []);
          setIssuedTotal(res.data.pagination?.total || 0);
          setIssuedPage(page);
        }
      })
      .catch(() => setError("Failed to load issued credentials"))
      .finally(() => setIssuedLoading(false));
  }, []);

  useEffect(() => {
    fetchKeys();
    fetchBadges();
    fetchSchools();
    fetchTemplates();
    CredentialService.getIssuerDomain().then((res: any) => {
      if (res?.data?.data?.customIssuerDomain) {
        setCustomIssuerDomain(res.data.data.customIssuerDomain);
      }
    }).catch(() => {});
  }, [fetchKeys, fetchBadges, fetchSchools, fetchTemplates]);

  useEffect(() => {
    if (activeTab === "issue") fetchIssued(1);
    if (activeTab === "templates") fetchTemplates();
  }, [activeTab, fetchIssued, fetchTemplates]);

  useEffect(() => {
    if (studentSearch.trim().length < 2) { setStudentResults([]); return; }
    const timer = setTimeout(() => {
      setSearchLoading(true);
      CredentialService.searchStudents(studentSearch.trim())
        .then((res) => { if (res.data?.status === 1) setStudentResults(res.data.data || []); })
        .catch(() => setError("Failed to search students"))
        .finally(() => setSearchLoading(false));
    }, 400);
    return () => clearTimeout(timer);
  }, [studentSearch]);

  useEffect(() => {
    if (attendanceCourseSearch.trim().length < 2) { setAttendanceCourseResults([]); return; }
    const timer = setTimeout(() => {
      CredentialService.searchCoursesForBadge(attendanceCourseSearch.trim())
        .then((res) => { if (res.data?.status === 1) setAttendanceCourseResults(res.data.data || []); })
        .catch(() => {});
    }, 400);
    return () => clearTimeout(timer);
  }, [attendanceCourseSearch]);

  // ── Handlers (unchanged) ──
  const handleCheckEligible = async () => {
    if (!selectedCourse) return;
    setEligibleLoading(true);
    setEligibleChecked(false);
    setBulkResult(null);
    try {
      const res = await CredentialService.getAttendanceBadgeEligible(selectedCourse.courseId);
      if (res.data?.status === 1) {
        setEligibleStudents(res.data.data.eligible || []);
        setAlreadyIssuedCount(res.data.data.alreadyIssued || 0);
        setEligibleChecked(true);
      } else {
        setError(res.data?.message || "Failed to check eligibility");
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to check eligibility");
    } finally {
      setEligibleLoading(false);
    }
  };

  const handleBulkIssue = async () => {
    if (!selectedCourse) return;
    setIssuingBulk(true);
    setError(null);
    try {
      const res = await CredentialService.issueAttendanceBadges(selectedCourse.courseId);
      if (res.data?.status === 1) {
        setBulkResult(res.data.data);
        setShowBulkConfirm(false);
        const { issued, skipped } = res.data.data;
        setSuccess(`Issued ${issued} Full Attendance badge(s)${skipped ? ` (${skipped} already had it)` : ""}`);
        setEligibleStudents([]);
        setEligibleChecked(false);
        fetchIssued(1);
      } else {
        setError(res.data?.message || "Failed to issue badges");
        setShowBulkConfirm(false);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to issue badges");
      setShowBulkConfirm(false);
    } finally {
      setIssuingBulk(false);
    }
  };

  const handleGenerateKey = async () => {
    setGeneratingKey(true);
    setError(null);
    try {
      const schoolId = keySchoolScope === "org" ? undefined : keySchoolScope;
      const res = await CredentialService.generateKeys(schoolId);
      if (res.data?.status === 1) {
        setSuccess(`New key generated: ${res.data.data.keyId}`);
        setShowKeyConfirm(false);
        fetchKeys();
      } else {
        setError(res.data?.message || "Failed to generate key");
      }
    } catch {
      setError("Failed to generate signing key");
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleSeedTemplates = async () => {
    setSeeding(true);
    try {
      const res = await CredentialService.seedTemplates();
      if (res.data?.status === 1) {
        setSuccess(res.data.message || "Templates seeded");
        fetchTemplates();
      } else {
        setError(res.data?.message || "Failed to seed templates");
      }
    } catch {
      setError("Failed to seed templates");
    } finally {
      setSeeding(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.content.trim()) {
      setError("Template name and content are required");
      return;
    }
    setTemplateSaving(true);
    try {
      if (editingTemplate) {
        await CredentialService.updateTemplate(editingTemplate.Id, templateForm);
        setSuccess("Template updated");
      } else {
        await CredentialService.createTemplate(templateForm);
        setSuccess("Template created");
      }
      setShowTemplateModal(false);
      fetchTemplates();
    } catch {
      setError("Failed to save template");
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleDuplicateTemplate = async (id: number) => {
    try {
      await CredentialService.duplicateTemplate(id);
      setSuccess("Template duplicated");
      fetchTemplates();
    } catch {
      setError("Failed to duplicate template");
    }
  };

  const handlePreviewTemplate = async (id: number) => {
    try {
      const res = await CredentialService.previewTemplate(id);
      setPreviewHtml(typeof res.data === "string" ? res.data : res.data?.data || "");
    } catch {
      setError("Failed to load preview");
    }
  };

  const openBadgeForm = (badge?: BadgeDef) => {
    if (badge) {
      const metadata = typeof badge.MetadataJSON === "string"
        ? (() => { try { return JSON.parse(badge.MetadataJSON); } catch { return {}; } })()
        : badge.MetadataJSON || {};
      setEditingBadge(badge);
      setBadgeForm({
        name: badge.Name,
        description: badge.Description || "",
        criteriaText: badge.CriteriaText || "",
        criteriaUrl: badge.CriteriaUrl || "",
        // Defensive: AchievementType can be empty on legacy rows that survived
        // the old enum's CHANGE COLUMN with values outside the new enum. Form
        // saves rejected with '' (DB enum doesn't accept empty), so fall back
        // to "Badge" so the admin can still edit + save.
        achievementType: badge.AchievementType || "Badge",
        expiryDurationDays: badge.ExpiryDurationDays?.toString() || "",
        certificateId: badge.CertificateId?.toString() || "",
        imagePath: badge.ImagePath || "",
        ctaUrl: badge.CtaUrl || "",
        ctaText: badge.CtaText || "",
        verifyTemplate: resolveVerifyTemplate(badge.VerifyTemplate),
        crossSellLinks: metadata.crossSellLinks || [],
      });
    } else {
      setEditingBadge(null);
      setBadgeForm({
        name: "", description: "", criteriaText: "", criteriaUrl: "",
        achievementType: "Badge", expiryDurationDays: "", certificateId: "",
        imagePath: "",
        ctaUrl: "", ctaText: "", verifyTemplate: "registrar", crossSellLinks: [],
      });
    }
    setShowBadgeModal(true);
  };

  const handleSaveBadge = async () => {
    if (!badgeForm.name.trim()) {
      setError("Badge name is required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: badgeForm.name.trim(),
        description: badgeForm.description.trim() || undefined,
        criteriaText: badgeForm.criteriaText.trim() || undefined,
        criteriaUrl: badgeForm.criteriaUrl.trim() || undefined,
        achievementType: badgeForm.achievementType,
        expiryDurationDays: badgeForm.expiryDurationDays ? parseInt(badgeForm.expiryDurationDays) : undefined,
        certificateId: badgeForm.certificateId ? parseInt(badgeForm.certificateId) : undefined,
        imagePath: badgeForm.imagePath.trim() || undefined,
        ctaUrl: badgeForm.ctaUrl.trim() || undefined,
        ctaText: badgeForm.ctaText.trim() || undefined,
        verifyTemplate: badgeForm.verifyTemplate,
        crossSellLinks: badgeForm.crossSellLinks.filter((l) => l.label.trim() && l.url.trim()),
      };

      if (editingBadge) {
        const res: any = await CredentialService.updateBadgeDefinition(editingBadge.Id, body);
        const backfilled = Number(res?.data?.data?.backfilledCount || 0);
        setSuccess(
          backfilled > 0
            ? `Badge definition updated. Backfilled certificate template on ${backfilled} existing credential${backfilled === 1 ? "" : "s"}.`
            : "Badge definition updated",
        );
      } else {
        await CredentialService.createBadgeDefinition(body);
        setSuccess("Badge definition created");
      }
      setShowBadgeModal(false);
      fetchBadges();
    } catch {
      setError("Failed to save badge definition");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBadge = async (id: number) => {
    try {
      await CredentialService.deleteBadgeDefinition(id);
      setSuccess("Badge definition deleted");
      fetchBadges();
    } catch {
      setError("Failed to delete badge definition");
    }
  };

  const openAppliesTo = async (badge: BadgeDef) => {
    setAppliesToBadge(badge);
    setShowAppliesToModal(true);
    setAppliesToLoading(true);
    try {
      const res: any = await CredentialService.getBadgeEligibility(badge.Id);
      const rows = (res?.data?.data || []).map((r: any) => ({
        scopeType: r.scopeType,
        scopeId: Number(r.scopeId),
        scopeLabel: r.scopeLabel || `#${r.scopeId}`,
      }));
      setAppliesToScopes(rows);
    } catch {
      setError("Failed to load eligibility");
      setAppliesToScopes([]);
    } finally {
      setAppliesToLoading(false);
    }
  };

  const addScope = (scopeType: "lesson_plan" | "course" | "program", id: number, name: string) => {
    setAppliesToScopes((prev) => {
      if (prev.some((s) => s.scopeType === scopeType && s.scopeId === id)) return prev;
      return [...prev, { scopeType, scopeId: id, scopeLabel: name }];
    });
  };

  const removeScope = (scopeType: string, id: number) => {
    setAppliesToScopes((prev) =>
      prev.filter((s) => !(s.scopeType === scopeType && s.scopeId === id)),
    );
  };

  const saveAppliesTo = async () => {
    if (!appliesToBadge) return;
    setAppliesToSaving(true);
    try {
      await CredentialService.saveBadgeEligibility(
        appliesToBadge.Id,
        appliesToScopes.map((s) => ({ scopeType: s.scopeType, scopeId: s.scopeId })),
      );
      setSuccess(`Applies-to updated for ${appliesToBadge.Name}`);
      setShowAppliesToModal(false);
    } catch {
      setError("Failed to save eligibility");
    } finally {
      setAppliesToSaving(false);
    }
  };

  const handleIssue = async () => {
    if (!selectedStudent) return;
    if (issueMode === "badge" && !selectedBadgeId) return;
    if (issueMode === "certificate" && !selectedIssueCertificateId) return;
    setIssuing(true);
    setError(null);
    setIssueResult(null);
    try {
      const res =
        issueMode === "badge"
          ? await CredentialService.adminIssueCredential({
              badgeDefinitionId: selectedBadgeId as number,
              enquiryId: selectedStudent.enquiryId,
            })
          : await CredentialService.adminIssueCertificate({
              certificateId: selectedIssueCertificateId as number,
              enquiryId: selectedStudent.enquiryId,
              courseName: issueCourseName.trim() || undefined,
            });
      if (res.data?.status === 1) {
        setIssueResult(res.data.data);
        setSuccess(
          res.data.data.alreadyExisted
            ? `${issueMode === "badge" ? "Badge" : "Certificate"} already existed for this student`
            : `${issueMode === "badge" ? "Badge" : "Certificate"} issued successfully`,
        );
        setShowIssueConfirm(false);
        fetchIssued(1);
      } else {
        setError(res.data?.message || "Failed to issue credential");
        setShowIssueConfirm(false);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to issue credential");
      setShowIssueConfirm(false);
    } finally {
      setIssuing(false);
    }
  };

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);
  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(null), 6000);
      return () => clearTimeout(t);
    }
  }, [error]);

  const activeKey = keys.find((k) => k.Status === "active");
  const builtInCount = templates.filter((t) => t.IsBuiltIn).length;

  // ── Tab body renderers ───────────────────────────────────────────────
  const renderKeysTab = () => (
    <>
      <div className="ca-card">
        <div className="ca-card-head">
          <span className="ca-card-icon"><KeyRound size={18} /></span>
          <h3>Signing Keys</h3>
        </div>
        <p className="ca-card-desc">
          Ed25519 signing keys cryptographically sign credentials (OB 3.0 / W3C Verifiable Credentials).
          Each school can have its own key, or use the organization-wide key.
        </p>

        <div className="ca-row ca-row-auto">
          <div>
            <label className="ca-field-label">Scope</label>
            <select
              className="ca-select"
              style={{ width: 280 }}
              value={keySchoolScope === "org" ? "org" : keySchoolScope}
              onChange={(e) =>
                setKeySchoolScope(e.target.value === "org" ? "org" : parseInt(e.target.value))
              }
            >
              <option value="org">Organization-wide</option>
              {schools.map((s) => (
                <option key={s.Id} value={s.Id}>{s.Name}</option>
              ))}
            </select>
          </div>
          <div />
          <button
            type="button"
            className="ca-btn ca-btn-primary"
            onClick={() => setShowKeyConfirm(true)}
          >
            <KeyRound size={14} /> Generate Key
          </button>
        </div>

        {keysLoading ? (
          <div className="ca-loading"><div className="ca-spinner" /> Loading signing keys…</div>
        ) : keys.length === 0 ? (
          <div className="ca-empty">
            <div className="ca-empty-icon"><KeyRound size={26} /></div>
            <h3>No signing keys yet</h3>
            <p>Generate one to start issuing signed credentials.</p>
            <button
              type="button"
              className="ca-btn ca-btn-primary"
              onClick={() => setShowKeyConfirm(true)}
            >
              <KeyRound size={14} /> Generate Your First Key
            </button>
          </div>
        ) : (
          <div className="ca-table-wrap">
            <table className="ca-table">
              <thead>
                <tr>
                  <th>Key ID</th>
                  <th>Scope</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Public Key (Multibase)</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.KeyId}>
                    <td className="ca-cell-mono">{k.KeyId}</td>
                    <td>{k.SchoolName || "Organization-wide"}</td>
                    <td>
                      <span className={`ca-pill ${k.Status === "active" ? "success" : "muted"}`}>
                        {k.Status}
                      </span>
                    </td>
                    <td className="ca-cell-mono" style={{ fontSize: 12 }}>{k.CreatedDate}</td>
                    <td className="ca-cell-mono ca-cell-truncate" title={k.PublicKeyMultibase}>
                      {k.PublicKeyMultibase}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {activeKey && (
        <div className="ca-info-card info">
          <h4><Globe size={14} /> DID Endpoints</h4>
          <p><strong>DID Document URL:</strong> <code>GET /api/v1/credentials/.well-known/did.json</code></p>
          <p><strong>Issuer DID:</strong> <code>did:web:{customIssuerDomain || "<tenant>.edusquares.com"}</code></p>
        </div>
      )}

      {activeKey && (
        <div className="ca-card">
          <div className="ca-card-head">
            <span className="ca-card-icon"><Globe size={18} /></span>
            <h3>DID Configuration</h3>
          </div>
          <p className="ca-card-desc">
            Set a custom issuer domain for decentralized verification. Credentials will use{" "}
            <code>did:web:your-domain.edu</code> instead of the default. You must host the DID document at{" "}
            <code>https://your-domain.edu/.well-known/did.json</code>.
          </p>
          <div className="ca-row ca-row-auto" style={{ marginBottom: 12 }}>
            <div style={{ minWidth: 320 }}>
              <label className="ca-field-label">Custom Issuer Domain</label>
              <input
                className="ca-input mono"
                value={customIssuerDomain}
                onChange={(e) => setCustomIssuerDomain(e.target.value)}
                placeholder="university.edu (leave empty for default)"
              />
            </div>
            <div />
            <button
              type="button"
              className="ca-btn ca-btn-primary"
              disabled={domainSaving}
              onClick={async () => {
                setDomainSaving(true);
                try {
                  await CredentialService.setIssuerDomain(customIssuerDomain.trim() || null);
                  setSuccess("Issuer domain updated");
                } catch {
                  setError("Failed to update issuer domain");
                } finally {
                  setDomainSaving(false);
                }
              }}
            >
              {domainSaving ? "Saving…" : "Save"}
            </button>
          </div>
          <button
            type="button"
            className="ca-btn ca-btn-secondary"
            onClick={() => {
              window.open(
                `${Config.nodeSiteUrl}v1/credentials/.well-known/did.json?download=true`,
                "_blank",
              );
            }}
          >
            <Download size={14} /> Download DID Document
          </button>
        </div>
      )}
    </>
  );

  const renderBadgesTab = () => (
    <div className="ca-card">
      <div className="ca-card-head">
        <span className="ca-card-icon"><Award size={18} /></span>
        <h3>Badge Definitions</h3>
      </div>
      <p className="ca-card-desc">
        Badge definitions describe achievements that can be issued as OB 3.0 credentials.
        Optionally link a certificate template so issuing the badge also generates a PDF.
      </p>
      <div
        style={{
          background: "var(--status-info-bg)",
          border: "1px solid #bfdbfe",
          borderRadius: 8,
          padding: "10px 14px",
          margin: "10px 0 14px",
          fontSize: 13,
          color: "var(--status-info-text)",
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
        }}
      >
        <span style={{ fontSize: 16, lineHeight: 1 }}>💡</span>
        <div>
          <strong>Badge or Certificate?</strong> Badges are for <em>mid-journey milestones</em>
          {" "}(topic mastery, participation streaks, contribution recognition). For a <em>formal
          completion</em> credential — course finished, program graduated, exam passed — create a{" "}
          <button
            type="button"
            onClick={() => setActiveTab("templates" as TabKey)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              color: "var(--brand-primary-hover)",
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Certificate Template
          </button>
          {" "}instead and issue it standalone from the Issue tab.
        </div>
      </div>
      <div className="ca-card-actions">
        <button type="button" className="ca-btn ca-btn-primary" onClick={() => openBadgeForm()}>
          <Award size={14} /> New Badge Definition
        </button>
      </div>

      {badgesLoading ? (
        <div className="ca-loading"><div className="ca-spinner" /> Loading badge definitions…</div>
      ) : badges.length === 0 ? (
        <div className="ca-empty">
          <div className="ca-empty-icon"><Award size={26} /></div>
          <h3>No badge definitions yet</h3>
          <p>Create one to define achievement criteria.</p>
          <button type="button" className="ca-btn ca-btn-primary" onClick={() => openBadgeForm()}>
            <Award size={14} /> Create First Badge
          </button>
        </div>
      ) : (
        <div className="ca-table-wrap">
          <table className="ca-table">
            <thead>
              <tr>
                <th>Badge</th>
                <th style={{ width: 160 }}>Created</th>
                <th style={{ width: 220 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {badges.map((b) => (
                <tr key={b.Id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{b.Name}</div>
                    {b.Description && (
                      <div className="ca-cell-muted">
                        {b.Description.length > 100 ? b.Description.slice(0, 100) + "…" : b.Description}
                      </div>
                    )}
                    <div className="ca-meta-row">
                      <span className="ca-pill info">{b.AchievementType}</span>
                      {b.certificateName ? (
                        <span
                          className="ca-meta-chip"
                          title={`Issuing this badge also generates a PDF using the "${b.certificateName}" certificate template.`}
                        >
                          <FileText size={11} /> + PDF: {b.certificateName}
                        </span>
                      ) : (
                        <span
                          className="ca-meta-chip ca-meta-chip-muted"
                          title="Badge-only credential — no PDF is generated when this badge is issued."
                        >
                          Badge only (no PDF)
                        </span>
                      )}
                      <span className="ca-meta-chip ca-meta-chip-muted">
                        {b.ExpiryDurationDays
                          ? `Expires in ${b.ExpiryDurationDays} days`
                          : "Never expires"}
                      </span>
                    </div>
                  </td>
                  <td style={{ fontSize: 12 }}>{formatIssuedDate(b.CreatedDate)}</td>
                  <td>
                    <div className="ca-cell-actions">
                      <button
                        type="button"
                        className="ca-btn ca-btn-text"
                        onClick={() => openBadgeForm(b)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="ca-btn ca-btn-text"
                        onClick={() => openAppliesTo(b)}
                      >
                        Applies&nbsp;To
                      </button>
                      <button
                        type="button"
                        className="ca-btn ca-btn-text-danger"
                        onClick={() => handleDeleteBadge(b.Id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderIssueTab = () => (
    <>
      {/* ── Single issue ── */}
      <div className="ca-card">
        <div className="ca-card-head">
          <span className="ca-card-icon"><UserCheck size={18} /></span>
          <h3>Issue Credential</h3>
        </div>
        <p className="ca-card-desc">
          Search for a student and select a badge definition to issue a signed credential.
        </p>

        <div className="ca-row" style={{ gridTemplateColumns: "1fr 1fr auto", alignItems: "flex-end" }}>
          <div>
            <label className="ca-field-label">Student *</label>
            {selectedStudent ? (
              <div className="ca-selected-pill">
                <UserCheck size={14} />
                <span>
                  <strong>{selectedStudent.name}</strong>
                  {selectedStudent.studentCode && (
                    <span style={{ marginLeft: 6, opacity: 0.7 }}>
                      ({selectedStudent.studentCode})
                    </span>
                  )}
                  {selectedStudent.email && (
                    <span style={{ marginLeft: 6, opacity: 0.7, fontSize: 12 }}>
                      {selectedStudent.email}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="ca-pill-clear"
                  onClick={() => {
                    setSelectedStudent(null);
                    setStudentSearch("");
                    setIssueResult(null);
                  }}
                  aria-label="Clear student"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="ca-search-wrap">
                <input
                  className="ca-input"
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                  placeholder="Search by name, student code, or email…"
                />
                {searchLoading && <div className="ca-search-empty">Searching…</div>}
                {studentResults.length > 0 && (
                  <div className="ca-search-dropdown">
                    {studentResults.map((s) => (
                      <div
                        key={s.enquiryId}
                        className="ca-search-item"
                        onClick={() => {
                          setSelectedStudent(s);
                          setStudentResults([]);
                          setStudentSearch("");
                          setIssueResult(null);
                        }}
                      >
                        <div><strong>{s.name}</strong></div>
                        <div className="ca-search-meta">
                          {s.studentCode && <span>{s.studentCode}</span>}
                          {s.studentCode && s.email && <span> · </span>}
                          {s.email && <span>{s.email}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="ca-field-label">Credential Type</label>
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              <button
                type="button"
                onClick={() => { setIssueMode("badge"); setIssueResult(null); }}
                style={{
                  padding: "6px 14px",
                  border: "1px solid",
                  borderColor: issueMode === "badge" ? "#6366f1" : "#cbd5e1",
                  background: issueMode === "badge" ? "#eef2ff" : "white",
                  color: issueMode === "badge" ? "#3730a3" : "#475569",
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                Badge (milestone)
              </button>
              <button
                type="button"
                onClick={() => { setIssueMode("certificate"); setIssueResult(null); }}
                style={{
                  padding: "6px 14px",
                  border: "1px solid",
                  borderColor: issueMode === "certificate" ? "#6366f1" : "#cbd5e1",
                  background: issueMode === "certificate" ? "#eef2ff" : "white",
                  color: issueMode === "certificate" ? "#3730a3" : "#475569",
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                Certificate (formal completion)
              </button>
            </div>

            {issueMode === "badge" ? (
              <>
                <label className="ca-field-label">Badge Definition *</label>
                <select
                  className="ca-select"
                  value={selectedBadgeId ?? ""}
                  onChange={(e) => {
                    setSelectedBadgeId(e.target.value ? parseInt(e.target.value) : null);
                    setIssueResult(null);
                  }}
                >
                  <option value="">Select a badge…</option>
                  {badges.map((b) => (
                    <option key={b.Id} value={b.Id}>{b.Name} ({b.AchievementType})</option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <label className="ca-field-label">Certificate Template *</label>
                <select
                  className="ca-select"
                  value={selectedIssueCertificateId ?? ""}
                  onChange={(e) => {
                    setSelectedIssueCertificateId(e.target.value ? parseInt(e.target.value) : null);
                    setIssueResult(null);
                  }}
                >
                  <option value="">Select a certificate template…</option>
                  {templates.map((t) => (
                    <option key={t.Id} value={t.Id}>
                      {t.Name} {t.IsBuiltIn ? "(Built-in)" : ""} — {t.Category}
                    </option>
                  ))}
                </select>
                <label className="ca-field-label" style={{ marginTop: 10 }}>Course / Program (optional)</label>
                <input
                  type="text"
                  className="ca-input"
                  value={issueCourseName}
                  onChange={(e) => setIssueCourseName(e.target.value)}
                  placeholder="e.g. BBA Completion"
                />
              </>
            )}
          </div>

          <button
            type="button"
            className="ca-btn ca-btn-primary"
            disabled={
              !selectedStudent ||
              issuing ||
              (issueMode === "badge" ? !selectedBadgeId : !selectedIssueCertificateId)
            }
            onClick={() => setShowIssueConfirm(true)}
          >
            <BadgeCheck size={14} /> Issue {issueMode === "badge" ? "Badge" : "Certificate"}
          </button>
        </div>

        {issueResult && (
          <div className="ca-info-card success" style={{ marginTop: 16, marginBottom: 0 }}>
            <h4><CheckCircle2 size={14} /> {issueResult.alreadyExisted ? "Credential already exists" : "Credential issued"}</h4>
            <p>
              <strong>{issueResult.recipientName}</strong> — {issueResult.badgeName}
              {issueResult.signed && <span className="ca-pill success" style={{ marginLeft: 10 }}>Signed</span>}
            </p>
            <p>
              <a
                href={issueResult.verificationUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 12, color: "var(--status-success-text)", textDecoration: "underline", wordBreak: "break-all" }}
              >
                {issueResult.verificationUrl}
              </a>
            </p>
          </div>
        )}
      </div>

      {/* ── Bulk attendance ── */}
      <div className="ca-card">
        <div className="ca-card-head">
          <span className="ca-card-icon"><Users size={18} /></span>
          <h3>Bulk Issue · Full Attendance Badges</h3>
        </div>
        <p className="ca-card-desc">
          Select a module, check eligible students (100% attendance), then issue badges in bulk.
        </p>

        <div className="ca-row" style={{ gridTemplateColumns: "1fr auto", alignItems: "flex-end" }}>
          <div>
            <label className="ca-field-label">Module *</label>
            {selectedCourse ? (
              <div className="ca-selected-pill">
                <FileText size={14} />
                <span>
                  <strong>{selectedCourse.name}</strong>
                  {selectedCourse.code && (
                    <span style={{ marginLeft: 6, opacity: 0.7, fontSize: 12 }}>{selectedCourse.code}</span>
                  )}
                </span>
                <button
                  type="button"
                  className="ca-pill-clear"
                  onClick={() => {
                    setSelectedCourse(null);
                    setAttendanceCourseSearch("");
                    setEligibleStudents([]);
                    setEligibleChecked(false);
                    setBulkResult(null);
                  }}
                  aria-label="Clear module"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="ca-search-wrap">
                <input
                  className="ca-input"
                  value={attendanceCourseSearch}
                  onChange={(e) => setAttendanceCourseSearch(e.target.value)}
                  placeholder="Search by module name or code…"
                />
                {attendanceCourseSearch.trim().length > 0 && attendanceCourseSearch.trim().length < 2 && (
                  <div className="ca-search-empty">Type at least 2 characters to search</div>
                )}
                {attendanceCourseResults.length > 0 && (
                  <div className="ca-search-dropdown">
                    {attendanceCourseResults.map((c) => (
                      <div
                        key={c.courseId}
                        className="ca-search-item"
                        onClick={() => {
                          setSelectedCourse(c);
                          setAttendanceCourseResults([]);
                          setAttendanceCourseSearch("");
                          setEligibleStudents([]);
                          setEligibleChecked(false);
                          setBulkResult(null);
                        }}
                      >
                        <div><strong>{c.name}</strong>
                          {c.code && <span className="ca-search-meta" style={{ display: "inline", marginLeft: 6 }}>{c.code}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            className="ca-btn ca-btn-secondary"
            disabled={!selectedCourse || eligibleLoading}
            onClick={handleCheckEligible}
          >
            <Search size={14} /> {eligibleLoading ? "Checking…" : "Check Eligible"}
          </button>
        </div>

        {eligibleChecked && (
          <div style={{ marginTop: 16 }}>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "12px 14px", background: "var(--surface-page)", border: "1px solid var(--border-default)",
              borderRadius: 10, marginBottom: 12,
            }}>
              <span style={{ fontSize: 14 }}>
                <strong style={{ color: "var(--text-strong)" }}>{eligibleStudents.length}</strong> student(s) eligible
                {alreadyIssuedCount > 0 && (
                  <span style={{ color: "var(--text-muted)" }}> · {alreadyIssuedCount} already issued</span>
                )}
              </span>
              {eligibleStudents.length > 0 && (
                <button
                  type="button"
                  className="ca-btn ca-btn-success ca-btn-sm"
                  disabled={issuingBulk}
                  onClick={() => setShowBulkConfirm(true)}
                >
                  <BadgeCheck size={14} /> Issue {eligibleStudents.length} Badge(s)
                </button>
              )}
            </div>

            {eligibleStudents.length > 0 && (
              <div className="ca-table-wrap">
                <table className="ca-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Email</th>
                      <th style={{ width: 120 }}>Attendance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligibleStudents.map((s: any) => (
                      <tr key={s.enquiryId}>
                        <td>{s.name}</td>
                        <td style={{ color: "var(--text-muted)", fontSize: 13 }}>{s.email || "—"}</td>
                        <td>
                          <span className="ca-pill success">{s.presentCount}/{s.totalCount}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {bulkResult && (
          <div className="ca-info-card success" style={{ marginTop: 16, marginBottom: 0 }}>
            <h4><CheckCircle2 size={14} /> Bulk issuance complete</h4>
            <p>
              Issued <strong>{bulkResult.issued}</strong> Full Attendance badge(s) for{" "}
              <strong>{bulkResult.courseName}</strong>
              {bulkResult.skipped > 0 && (
                <span style={{ opacity: 0.75 }}> ({bulkResult.skipped} already had it)</span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* ── Issued credentials ── */}
      <div className="ca-card">
        <div className="ca-card-head">
          <span className="ca-card-icon"><BadgeCheck size={18} /></span>
          <h3>Issued Credentials <span style={{ color: "var(--text-muted)", fontWeight: 500 }}>({issuedTotal})</span></h3>
        </div>

        {issuedLoading ? (
          <div className="ca-loading"><div className="ca-spinner" /> Loading credentials…</div>
        ) : issuedCredentials.length === 0 ? (
          <div className="ca-empty">
            <div className="ca-empty-icon"><BadgeCheck size={26} /></div>
            <h3>No credentials issued yet</h3>
            <p>Issued credentials will appear here.</p>
          </div>
        ) : (
          <>
            <div className="ca-table-wrap">
              <table className="ca-table">
                <thead>
                  <tr>
                    <th>Recipient</th>
                    <th>Badge / Certificate</th>
                    <th>Issued Date</th>
                    <th>Status</th>
                    <th>Verification</th>
                  </tr>
                </thead>
                <tbody>
                  {issuedCredentials.map((c) => (
                    <tr key={c.Id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{c.RecipientName}</div>
                        {c.RecipientEmail && <div className="ca-cell-muted">{c.RecipientEmail}</div>}
                      </td>
                      <td>{c.badgeName || c.certificateName || c.CourseName || "—"}</td>
                      <td style={{ fontSize: 13, whiteSpace: "nowrap" }}>{formatIssuedDate(c.IssuedDate)}</td>
                      <td>
                        <span className={`ca-pill ${c.Status === 1 ? "success" : "danger"}`}>
                          {c.Status === 1 ? "Active" : "Revoked"}
                        </span>
                      </td>
                      <td>
                        <a
                          href={c.verificationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 13, color: "var(--brand-primary)", display: "inline-flex", alignItems: "center", gap: 4 }}
                        >
                          Verify <ExternalLink size={11} />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {issuedTotal > 20 && (
              <div className="ca-pagination">
                <button
                  type="button"
                  className="ca-btn ca-btn-secondary ca-btn-sm"
                  disabled={issuedPage <= 1}
                  onClick={() => fetchIssued(issuedPage - 1)}
                >
                  Previous
                </button>
                <span>Page {issuedPage} of {Math.ceil(issuedTotal / 20)}</span>
                <button
                  type="button"
                  className="ca-btn ca-btn-secondary ca-btn-sm"
                  disabled={issuedPage >= Math.ceil(issuedTotal / 20)}
                  onClick={() => fetchIssued(issuedPage + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const renderTemplatesTab = () => (
    <div className="ca-card">
      <div className="ca-card-head">
        <span className="ca-card-icon"><FileText size={18} /></span>
        <h3>Certificate Templates</h3>
      </div>
      <p className="ca-card-desc">
        Certificate templates define the HTML layout for PDF generation. Built-in templates cannot
        be edited directly — duplicate one to customize it.
      </p>
      <div className="ca-card-actions">
        <button
          type="button"
          className="ca-btn ca-btn-primary"
          onClick={() => {
            setEditingTemplate(null);
            setTemplateForm({ name: "", content: "", category: "general", remarks: "" });
            setShowTemplateModal(true);
          }}
        >
          <FileText size={14} /> Create Custom Template
        </button>
        {builtInCount === 0 && (
          <button
            type="button"
            className="ca-btn ca-btn-secondary"
            onClick={handleSeedTemplates}
            disabled={seeding}
          >
            <RefreshCw size={14} /> {seeding ? "Seeding…" : "Seed Built-in Templates"}
          </button>
        )}
      </div>

      {templatesLoading ? (
        <div className="ca-loading"><div className="ca-spinner" /> Loading templates…</div>
      ) : templates.length === 0 ? (
        <div className="ca-empty">
          <div className="ca-empty-icon"><FileText size={26} /></div>
          <h3>No templates yet</h3>
          <p>Seed built-in templates or create a custom one.</p>
        </div>
      ) : (
        <div className="ca-tpl-grid">
          {templates.map((t) => (
            <div key={t.Id} className="ca-tpl-card">
              <div className="ca-tpl-head">
                <div>
                  <div className="ca-tpl-name">{t.Name}</div>
                  <div className="ca-tpl-meta">
                    <span>{t.Category || "general"}</span>
                    {t.IsBuiltIn ? <span className="ca-pill builtin">Built-in</span> : null}
                  </div>
                </div>
                <span className="ca-tpl-id">ID: {t.Id}</span>
              </div>
              <div className="ca-tpl-actions">
                <button
                  type="button"
                  className="ca-btn ca-btn-secondary ca-btn-sm"
                  onClick={() => handlePreviewTemplate(t.Id)}
                >
                  Preview
                </button>
                <button
                  type="button"
                  className="ca-btn ca-btn-secondary ca-btn-sm"
                  onClick={() => handleDuplicateTemplate(t.Id)}
                >
                  Duplicate
                </button>
                {!t.IsBuiltIn && (
                  <button
                    type="button"
                    className="ca-btn ca-btn-secondary ca-btn-sm"
                    onClick={() => {
                      setEditingTemplate(t);
                      setTemplateForm({
                        name: t.Name, content: "",
                        category: t.Category || "general", remarks: "",
                      });
                      setShowTemplateModal(true);
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── Top-level render ────────────────────────────────────────────────
  return (
    <div className="ca-page">
      <aside className="ca-side">
        <h2>Credential Admin</h2>
        <button
          type="button"
          className={`ca-nav-btn ${activeTab === "keys" ? "active" : ""}`}
          onClick={() => setActiveTab("keys")}
        >
          <KeyRound size={16} className="ca-nav-icon" />
          <span className="ca-nav-label">Signing Keys</span>
          <span className="ca-nav-pill">{keys.length}</span>
        </button>
        <button
          type="button"
          className={`ca-nav-btn ${activeTab === "badges" ? "active" : ""}`}
          onClick={() => setActiveTab("badges")}
        >
          <Award size={16} className="ca-nav-icon" />
          <span className="ca-nav-label">Badge Definitions</span>
          <span className="ca-nav-pill">{badges.length}</span>
        </button>
        <button
          type="button"
          className={`ca-nav-btn ${activeTab === "issue" ? "active" : ""}`}
          onClick={() => setActiveTab("issue")}
        >
          <BadgeCheck size={16} className="ca-nav-icon" />
          <span className="ca-nav-label">Issue Credentials</span>
        </button>
        <button
          type="button"
          className={`ca-nav-btn ${activeTab === "templates" ? "active" : ""}`}
          onClick={() => setActiveTab("templates")}
        >
          <FileText size={16} className="ca-nav-icon" />
          <span className="ca-nav-label">Certificate Templates</span>
          <span className="ca-nav-pill">{templates.length}</span>
        </button>
        <button
          type="button"
          className={`ca-nav-btn ${activeTab === "rules" ? "active" : ""}`}
          onClick={() => setActiveTab("rules")}
        >
          <Brain size={16} className="ca-nav-icon" />
          <span className="ca-nav-label">Issuance Rules</span>
        </button>

        <div className="ca-divider" style={{ margin: "12px 8px" }} />
        <button
          type="button"
          className="ca-nav-btn"
          onClick={() => history.push("/tenant-preferences")}
          style={{ fontSize: 13, color: "var(--text-muted)" }}
          title="Back to tenant preferences hub"
        >
          <ShieldCheck size={14} className="ca-nav-icon" />
          <span className="ca-nav-label">Tenant Preferences</span>
        </button>
      </aside>

      <main className="ca-main">
        <div className="ca-header">
          <div>
            <h1>
              {activeTab === "keys" && "Signing Keys"}
              {activeTab === "badges" && "Badge Definitions"}
              {activeTab === "issue" && "Issue Credentials"}
              {activeTab === "templates" && "Certificate Templates"}
              {activeTab === "rules" && "Issuance Rules"}
            </h1>
            <p className="ca-subtitle">
              {activeTab === "keys" &&
                "Ed25519 keypairs that cryptographically sign all OB 3.0 verifiable credentials. Rotate per-school or org-wide."}
              {activeTab === "badges" &&
                "Reusable badge definitions linked to certificate templates. These are what you issue to students."}
              {activeTab === "issue" &&
                "Issue a signed credential to a single student, bulk-issue Full Attendance badges for a module, and review all credentials already issued."}
              {activeTab === "templates" &&
                "HTML templates used for certificate PDF generation. Built-in templates can be duplicated and customised."}
              {activeTab === "rules" &&
                "Predicate-based auto-issuance — define rules that fire on student events (test scores, attendance, completion, time-based). Powered by the badge-rules-eval-drain job."}
            </p>
          </div>
        </div>

        {error && (
          <div className="ca-alert ca-alert-error">
            <AlertCircle size={16} className="ca-alert-icon" />
            <div className="ca-alert-body">{error}</div>
            <button type="button" className="ca-alert-close" onClick={() => setError(null)}>&times;</button>
          </div>
        )}
        {success && (
          <div className="ca-alert ca-alert-success">
            <CheckCircle2 size={16} className="ca-alert-icon" />
            <div className="ca-alert-body">{success}</div>
            <button type="button" className="ca-alert-close" onClick={() => setSuccess(null)}>&times;</button>
          </div>
        )}

        {activeTab === "keys" && renderKeysTab()}
        {activeTab === "badges" && renderBadgesTab()}
        {activeTab === "issue" && renderIssueTab()}
        {activeTab === "templates" && renderTemplatesTab()}
        {activeTab === "rules" && (
          <RulesTab
            badges={badges.map((b) => ({
              Id: b.Id,
              Name: b.Name,
              AchievementType: b.AchievementType,
            }))}
          />
        )}
      </main>

      {/* ══════════ Template Preview Modal ══════════ */}
      <Modal
        show={!!previewHtml}
        onHide={() => setPreviewHtml(null)}
        size="lg"
        dialogClassName="ca-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Template Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewHtml && <ScaledCertificatePreview html={previewHtml} />}
        </Modal.Body>
      </Modal>

      {/* ══════════ Template Edit/Create Modal ══════════ */}
      <Modal
        show={showTemplateModal}
        onHide={() => setShowTemplateModal(false)}
        size="lg"
        dialogClassName="ca-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{editingTemplate ? "Edit Template" : "Create Custom Template"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="Template name"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={templateForm.category}
                onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
              >
                <option value="general">General</option>
                <option value="completion">Completion</option>
                <option value="participation">Participation</option>
                <option value="achievement">Achievement</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>HTML Content *</Form.Label>
              <Form.Control
                as="textarea"
                rows={12}
                value={templateForm.content}
                onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })}
                placeholder="Paste HTML template here. Use {{recipientName}}, {{courseName}}, {{organizationName}}, {{issuedDate}}, {{credentialUUID}}, {{verificationUrl}}, {{completionDate}}, {{certificateName}} as variables."
                style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12 }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTemplateModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveTemplate} disabled={templateSaving}>
            {templateSaving ? "Saving…" : editingTemplate ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════ Issue Confirmation Modal ══════════ */}
      <Modal show={showIssueConfirm} onHide={() => setShowIssueConfirm(false)} dialogClassName="ca-modal">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Credential Issuance</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Issue credential to:</p>
          <p>
            <strong>{selectedStudent?.name}</strong>
            {selectedStudent?.email && (
              <span style={{ color: "var(--text-muted)" }}> ({selectedStudent.email})</span>
            )}
          </p>
          {issueMode === "badge" ? (
            <p>
              Badge: <strong>{badges.find((b) => b.Id === selectedBadgeId)?.Name}</strong>
            </p>
          ) : (
            <>
              <p>
                Certificate: <strong>{templates.find((t) => t.Id === selectedIssueCertificateId)?.Name}</strong>
              </p>
              {issueCourseName.trim() && (
                <p>
                  Course: <strong>{issueCourseName.trim()}</strong>
                </p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowIssueConfirm(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleIssue} disabled={issuing}>
            {issuing ? "Issuing…" : "Confirm Issue"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════ Bulk Issue Confirmation Modal ══════════ */}
      <Modal show={showBulkConfirm} onHide={() => setShowBulkConfirm(false)} dialogClassName="ca-modal">
        <Modal.Header closeButton>
          <Modal.Title>Issue Full Attendance Badges</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Issue <strong>Full Attendance</strong> badges to{" "}
            <strong>{eligibleStudents.length}</strong> student(s) for:
          </p>
          <p><strong>{selectedCourse?.name}</strong></p>
          <p style={{ color: "var(--text-muted)", fontSize: 13 }}>
            Each student will receive a signed OB 3.0 verifiable credential.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkConfirm(false)}>Cancel</Button>
          <Button variant="success" onClick={handleBulkIssue} disabled={issuingBulk}>
            {issuingBulk ? "Issuing…" : `Issue ${eligibleStudents.length} Badge(s)`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════ Generate Key Confirmation Modal ══════════ */}
      <Modal show={showKeyConfirm} onHide={() => setShowKeyConfirm(false)} dialogClassName="ca-modal">
        <Modal.Header closeButton>
          <Modal.Title>Generate Signing Key</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This will generate a new Ed25519 signing keypair for{" "}
            <strong>
              {keySchoolScope === "org"
                ? "the organization"
                : schools.find((s) => s.Id === keySchoolScope)?.Name || "the selected school"}
            </strong>.
          </p>
          {activeKey && (
            <p style={{ color: "var(--status-warning-text)" }}>
              The current active key for this scope (<code>{activeKey.KeyId}</code>) will be rotated.
              Existing credentials signed with it remain valid.
            </p>
          )}
          <p>Continue?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowKeyConfirm(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleGenerateKey} disabled={generatingKey}>
            {generatingKey ? "Generating…" : "Generate Key"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════ Badge Definition Form Modal ══════════ */}
      <Modal
        show={showBadgeModal}
        onHide={() => setShowBadgeModal(false)}
        size="xl"
        dialogClassName="ca-badge-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{editingBadge ? "Edit Badge Definition" : "New Badge Definition"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!editingBadge && (
            <div
              style={{
                background: "var(--status-warning-bg)",
                border: "1px solid var(--status-warning-border)",
                borderRadius: 8,
                padding: "10px 14px",
                marginBottom: 16,
                fontSize: 13,
                color: "var(--status-warning-text)",
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <span style={{ fontSize: 16, lineHeight: 1 }}>💡</span>
              <div>
                <strong>Is this really a badge?</strong> Badges are for mid-journey milestones —
                topic mastery, participation streaks, contribution recognition. If you're issuing
                this for a <em>completion</em> (course finished, program graduated, exam passed),
                you probably want a <strong>Certificate Template</strong> instead. Cancel this and
                open the Templates tab.
              </div>
            </div>
          )}
          <div className="bm-grid">
            <div className="bm-form-col">
              <Form>
                <Section icon={<TypeIcon size={15} />} title="Identity" subtitle="Required">
                  <Form.Group>
                    <Form.Label>Name <span style={{ color: "var(--status-danger)" }}>*</span></Form.Label>
                    <Form.Control
                      value={badgeForm.name}
                      onChange={(e) => {
                        const val = e.target.value;
                        setBadgeForm((prev) => ({ ...prev, name: val }));
                      }}
                      onBlur={() => {
                        setBadgeForm((prev) => {
                          if (!prev.name.trim()) return prev;
                          const defaults = generateBadgeDefaults(prev.name, prev.achievementType);
                          const desc = !prev.description ? defaults.description : prev.description;
                          const crit = !prev.criteriaText ? defaults.criteriaText : prev.criteriaText;
                          if (desc === prev.description && crit === prev.criteriaText) return prev;
                          return { ...prev, description: desc, criteriaText: crit };
                        });
                      }}
                      placeholder="e.g. Responsible AI User — Level 1"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={badgeForm.description}
                      onChange={(e) => setBadgeForm({ ...badgeForm, description: e.target.value })}
                      placeholder="One-line summary that prospects will see"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Criteria</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={badgeForm.criteriaText}
                      onChange={(e) => setBadgeForm({ ...badgeForm, criteriaText: e.target.value })}
                      placeholder="What must the student do to earn this?"
                    />
                  </Form.Group>
                </Section>

                <Section icon={<Award size={15} />} title="Achievement">
                  <Form.Group>
                    <Form.Label>Achievement Type</Form.Label>
                    <div className="bm-at-grid">
                      {ACHIEVEMENT_TYPE_OPTIONS.map((opt) => {
                        const isActive = badgeForm.achievementType === opt.value;
                        return (
                          <button
                            type="button"
                            key={opt.value}
                            className={`bm-at-card ${isActive ? "is-active" : ""}`}
                            onClick={() => {
                              const oldDefaults = generateBadgeDefaults(badgeForm.name, badgeForm.achievementType);
                              const newDefaults = generateBadgeDefaults(badgeForm.name, opt.value);
                              setBadgeForm((prev) => ({
                                ...prev,
                                achievementType: opt.value,
                                description:
                                  !prev.description || prev.description === oldDefaults.description
                                    ? newDefaults.description
                                    : prev.description,
                                criteriaText:
                                  !prev.criteriaText || prev.criteriaText === oldDefaults.criteriaText
                                    ? newDefaults.criteriaText
                                    : prev.criteriaText,
                              }));
                            }}
                          >
                            <span className="bm-at-card-icon" aria-hidden>
                              <span style={{ fontSize: 16 }}>{opt.emoji}</span>
                            </span>
                            <span>
                              <span className="bm-at-card-title">{opt.label}</span>
                              <span className="bm-at-card-sub">{opt.sub}</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Validity (days)</Form.Label>
                    <Form.Control
                      type="number"
                      value={badgeForm.expiryDurationDays}
                      onChange={(e) => setBadgeForm({ ...badgeForm, expiryDurationDays: e.target.value })}
                      placeholder="Leave empty for permanent"
                    />
                  </Form.Group>
                </Section>

                <Section icon={<ImageIcon size={15} />} title="Imagery">
                  <BadgeImagePicker
                    value={badgeForm.imagePath}
                    onChange={(url) => setBadgeForm({ ...badgeForm, imagePath: url })}
                    badgeDefinitionId={editingBadge?.Id}
                  />
                </Section>

                <Section icon={<Scroll size={15} />} title="Linked Certificate" subtitle="Optional">
                  <Form.Group>
                    <Form.Label>Certificate Template</Form.Label>
                    <Form.Select
                      value={badgeForm.certificateId}
                      onChange={(e) => setBadgeForm({ ...badgeForm, certificateId: e.target.value })}
                    >
                      <option value="">No template (badge only, no PDF)</option>
                      {templates.map((t) => (
                        <option key={t.Id} value={t.Id}>
                          {t.Name} {t.IsBuiltIn ? "(Built-in)" : ""} — {t.Category}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      When linked, issuing this badge also generates a downloadable certificate PDF.
                    </Form.Text>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>
                      Criteria URL
                      <FieldHint summary="OB 3.0 link to the formal rubric / syllabus">
                        <div>
                          <strong>What this does:</strong> Embeds a link in the signed Open Badges 3.0
                          credential pointing to the authoritative description of how this badge is
                          earned — rubric, syllabus, or assessment plan.
                        </div>
                        <div>
                          <strong>Who reads it:</strong> Employers, accreditation reviewers, and
                          verification platforms (LinkedIn, Credly) that want the formal definition
                          rather than the marketing description.
                        </div>
                        <div>
                          <strong>When to fill it in:</strong> For accredited courses or regulated
                          programs, link to your published program / syllabus page. For everyday
                          milestone badges, leave this empty — the Criteria text above is enough.
                        </div>
                      </FieldHint>
                    </Form.Label>
                    <Form.Control
                      value={badgeForm.criteriaUrl}
                      onChange={(e) => setBadgeForm({ ...badgeForm, criteriaUrl: e.target.value })}
                      placeholder="https://… (optional — leave blank for milestone badges)"
                    />
                  </Form.Group>
                </Section>

                <CollapsibleSection
                  icon={<Palette size={15} />}
                  title="Verification page"
                  subtitle="Advanced"
                >
                  <Form.Group>
                    <Form.Label>
                      Page Design
                      <FieldHint summary="Look-and-feel of the public verification page">
                        <div>
                          Picks the visual design of the public verification page that anyone with
                          the badge URL can open. The credential data is the same in every design
                          — only the framing, colours, and layout differ.
                        </div>
                        <div>
                          <strong>Registrar</strong> — engraved ivory certificate with a gold seal;
                          formal, celebrates the learner. <strong>Ledger</strong> — corporate
                          verification report with a cryptographic checklist; best when employers
                          will check the credential. <strong>Onyx</strong> — dark and dramatic with
                          gold accents; built for sharing on LinkedIn. <strong>Folio</strong> —
                          clean editorial minimal; modern professional audiences.
                        </div>
                        <div>
                          Preview any design on an issued credential by adding
                          <code> ?template=registrar|ledger|onyx|folio</code> to its verification URL.
                        </div>
                      </FieldHint>
                    </Form.Label>
                    <Form.Select
                      value={badgeForm.verifyTemplate}
                      onChange={(e) => setBadgeForm({ ...badgeForm, verifyTemplate: e.target.value })}
                    >
                      <option value="registrar">Registrar (engraved certificate, formal)</option>
                      <option value="ledger">Ledger (verification report, corporate)</option>
                      <option value="onyx">Onyx (dark prestige, share-worthy)</option>
                      <option value="folio">Folio (editorial minimal, modern)</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>
                      CTA Button URL
                      <FieldHint summary='Link behind the "I want to earn this" button on the verification page'>
                        <div>
                          When a viewer lands on the public verification page (because someone
                          shared the badge on LinkedIn, in a CV, etc.), a CTA button invites them
                          to enrol themselves. This URL is where that button takes them.
                        </div>
                        <div>
                          <strong>Easiest path:</strong> use the <em>Pick from products</em> picker
                          below to auto-fill the URL with the product's ilearn page. You can still
                          paste any URL manually for off-platform funnels.
                        </div>
                      </FieldHint>
                    </Form.Label>
                    <ProductPicker
                      onPick={(p, url) =>
                        setBadgeForm((prev) => ({
                          ...prev,
                          ctaUrl: url,
                          // Only seed CTA text if the admin hasn't typed their own.
                          ctaText: prev.ctaText.trim() ? prev.ctaText : `Enrol in ${p.name}`,
                        }))
                      }
                    />
                    <Form.Control
                      value={badgeForm.ctaUrl}
                      onChange={(e) => setBadgeForm({ ...badgeForm, ctaUrl: e.target.value })}
                      placeholder="https://your-enrollment-page.com"
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>CTA Button Text</Form.Label>
                    <Form.Control
                      value={badgeForm.ctaText}
                      onChange={(e) => setBadgeForm({ ...badgeForm, ctaText: e.target.value })}
                      placeholder="I want to earn this too!"
                    />
                  </Form.Group>
                </CollapsibleSection>

                <CollapsibleSection
                  icon={<Megaphone size={15} />}
                  title="Cross-sell on verification page"
                  subtitle={`${badgeForm.crossSellLinks.length}/6`}
                >
                  <Form.Text className="text-muted d-block mb-2">
                    Showcase other courses / programmes on the verification page — visitors can click to learn more.
                  </Form.Text>
                  {badgeForm.crossSellLinks.map((link, idx) => (
                    <div key={idx} className="ca-crosssell-card">
                      <div className="bm-crosssell-head">
                        <span className="bm-crosssell-num">Course {idx + 1}</span>
                        <div className="bm-crosssell-actions">
                          <ProductPicker
                            variant="compact"
                            triggerLabel={link.label ? "Replace product" : "Pick a product"}
                            onPick={(p, url) => {
                              const updated = [...badgeForm.crossSellLinks];
                              updated[idx] = {
                                ...updated[idx],
                                // Only seed empty fields — never overwrite admin edits
                                label: updated[idx].label.trim() || p.name,
                                url,
                                imageUrl: updated[idx].imageUrl?.trim() || p.imageUrl || "",
                                description:
                                  updated[idx].description?.trim() ||
                                  (p.category || p.partnerName || "") as string,
                              };
                              setBadgeForm({ ...badgeForm, crossSellLinks: updated });
                            }}
                          />
                          <button
                            type="button"
                            className="bm-crosssell-remove"
                            onClick={() => {
                              const updated = badgeForm.crossSellLinks.filter((_, i) => i !== idx);
                              setBadgeForm({ ...badgeForm, crossSellLinks: updated });
                            }}
                          >
                            <X size={12} />
                            Remove
                          </button>
                        </div>
                      </div>
                      {link.imageUrl ? (
                        <div className="bm-crosssell-thumb-preview">
                          <img src={link.imageUrl} alt="" />
                          <span>Tile image — visitors see this on the verification page.</span>
                        </div>
                      ) : null}
                      <div className="bm-crosssell-fields">
                        <Form.Control
                          size="sm"
                          value={link.label}
                          onChange={(e) => {
                            const updated = [...badgeForm.crossSellLinks];
                            updated[idx] = { ...updated[idx], label: e.target.value };
                            setBadgeForm({ ...badgeForm, crossSellLinks: updated });
                          }}
                          placeholder="Course / program title"
                        />
                        <Form.Control
                          size="sm"
                          value={link.description || ""}
                          onChange={(e) => {
                            const updated = [...badgeForm.crossSellLinks];
                            updated[idx] = { ...updated[idx], description: e.target.value };
                            setBadgeForm({ ...badgeForm, crossSellLinks: updated });
                          }}
                          placeholder="Short tagline"
                        />
                        <div className="d-flex gap-2">
                          <Form.Control
                            size="sm"
                            value={link.url}
                            onChange={(e) => {
                              const updated = [...badgeForm.crossSellLinks];
                              updated[idx] = { ...updated[idx], url: e.target.value };
                              setBadgeForm({ ...badgeForm, crossSellLinks: updated });
                            }}
                            placeholder="Link URL"
                            style={{ flex: 2 }}
                          />
                          <Form.Control
                            size="sm"
                            value={link.imageUrl || ""}
                            onChange={(e) => {
                              const updated = [...badgeForm.crossSellLinks];
                              updated[idx] = { ...updated[idx], imageUrl: e.target.value };
                              setBadgeForm({ ...badgeForm, crossSellLinks: updated });
                            }}
                            placeholder="Image URL (optional)"
                            style={{ flex: 2 }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {badgeForm.crossSellLinks.length < 6 && (
                    <button
                      type="button"
                      className="bm-crosssell-add"
                      onClick={() =>
                        setBadgeForm({
                          ...badgeForm,
                          crossSellLinks: [
                            ...badgeForm.crossSellLinks,
                            { label: "", url: "", imageUrl: "", description: "" },
                          ],
                        })
                      }
                    >
                      + Add Course
                    </button>
                  )}
                </CollapsibleSection>
              </Form>
            </div>
            <div className="bm-preview-col">
              <BadgePreview
                name={badgeForm.name}
                description={badgeForm.description}
                achievementType={badgeForm.achievementType}
                imagePath={badgeForm.imagePath}
                expiryDurationDays={badgeForm.expiryDurationDays}
                certificateLinked={!!badgeForm.certificateId}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBadgeModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveBadge} disabled={saving}>
            {saving ? "Saving…" : editingBadge ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ───── Applies To (badge eligibility) ───── */}
      <Modal
        show={showAppliesToModal}
        onHide={() => setShowAppliesToModal(false)}
        size="lg"
        centered
        dialogClassName="ca-modal at-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Award size={18} style={{ marginRight: 8, verticalAlign: -3, color: "var(--brand-primary)" }} />
            Applies to — {appliesToBadge?.Name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="at-intro">
            Pick the learning modules, courses, or programs that should auto-issue this badge.
            A student qualifies the moment they complete every step of the chosen scope.
          </div>

          {appliesToLoading ? (
            <div className="at-loading"><span className="at-spinner" /> Loading current scopes…</div>
          ) : (
            <>
              <ScopePicker
                key={`lp-${appliesToBadge?.Id ?? 0}`}
                scope="lesson_plan"
                label="Learning Modules"
                icon={<FileText size={14} strokeWidth={2.2} />}
                attached={appliesToScopes.filter((s) => s.scopeType === "lesson_plan")}
                onAdd={(id, name) => addScope("lesson_plan", id, name)}
                onRemove={(id) => removeScope("lesson_plan", id)}
              />
              <ScopePicker
                key={`co-${appliesToBadge?.Id ?? 0}`}
                scope="course"
                label="Courses"
                icon={<BookOpen size={14} strokeWidth={2.2} />}
                attached={appliesToScopes.filter((s) => s.scopeType === "course")}
                onAdd={(id, name) => addScope("course", id, name)}
                onRemove={(id) => removeScope("course", id)}
              />
              <ScopePicker
                key={`pg-${appliesToBadge?.Id ?? 0}`}
                scope="program"
                label="Programs"
                icon={<GraduationCap size={14} strokeWidth={2.2} />}
                attached={appliesToScopes.filter((s) => s.scopeType === "program")}
                onAdd={(id, name) => addScope("program", id, name)}
                onRemove={(id) => removeScope("program", id)}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="at-footer-summary">
            {appliesToScopes.length === 0 ? (
              <span style={{ color: "var(--status-warning-text)" }}>
                <AlertCircle size={13} style={{ verticalAlign: -2, marginRight: 4 }} />
                No scopes selected — this badge will not auto-issue.
              </span>
            ) : (
              <>
                <CheckCircle2 size={13} style={{ verticalAlign: -2, marginRight: 4, color: "var(--status-success)" }} />
                {appliesToScopes.length} scope{appliesToScopes.length === 1 ? "" : "s"} selected
              </>
            )}
          </div>
          <Button variant="secondary" onClick={() => setShowAppliesToModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={saveAppliesTo} disabled={appliesToSaving || appliesToLoading}>
            {appliesToSaving ? "Saving…" : "Save changes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CredentialAdmin;
